/**
 * Central version gate for Backend-UI (web only).
 *
 * Reads Player-API `GET /v1/client-config` and compares against the build id
 * stamped at Vite build time (`VITE_APP_BUILD_ID`). When the server requires a
 * newer build and `forceReload` is true, clears Cache Storage and hard-reloads.
 *
 * Fail-open: if the config endpoint is unreachable, the console still loads so
 * an API outage never locks every agent out.
 */

export type BackendUiPolicy = {
  minBuildId: string
  forceReload: boolean
}

export type ClientConfig = {
  frontendUi?: unknown
  backendUi: BackendUiPolicy
}

export function getAppBuildId(): string {
  const raw = import.meta.env.VITE_APP_BUILD_ID
  if (typeof raw === 'string' && raw.trim()) return raw.trim()
  return 'dev'
}

export function needsForcedReload(
  policy: BackendUiPolicy | null | undefined,
  currentBuildId: string = getAppBuildId(),
): boolean {
  if (!policy?.forceReload) return false
  const required = (policy.minBuildId || '').trim()
  if (!required) return false
  const current = currentBuildId.trim()
  if (!current) return false
  return current !== required
}

export async function fetchClientConfig(baseUrl: string): Promise<ClientConfig | null> {
  const base = baseUrl.replace(/\/$/, '')
  try {
    const res = await fetch(`${base}/v1/client-config`, {
      method: 'GET',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
    if (!res.ok) return null
    const body = (await res.json()) as Partial<ClientConfig>
    const backend = body.backendUi
    if (!backend || typeof backend !== 'object') return null
    return {
      frontendUi: body.frontendUi,
      backendUi: {
        minBuildId: typeof backend.minBuildId === 'string' ? backend.minBuildId : '',
        forceReload: backend.forceReload === true,
      },
    }
  } catch {
    return null
  }
}

/** True when the running SPA is behind same-origin `/version.json` (every deploy). */
export function webDeployNeedsReload(
  currentBuildId: string,
  deployedBuildId: string,
): boolean {
  const current = currentBuildId.trim()
  const deployed = deployedBuildId.trim()
  if (!current || !deployed) return false
  return current !== deployed
}

/** Fail-open: `null` means do not force reload from the deploy stamp. */
export async function fetchDeployedVersion(): Promise<{ buildId: string } | null> {
  try {
    const res = await fetch('/version.json', {
      method: 'GET',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
    if (!res.ok) return null
    const body = (await res.json()) as Record<string, unknown>
    const raw = body.buildId ?? body.webBuildId ?? body.id
    const buildId = typeof raw === 'string' ? raw.trim() : ''
    if (!buildId) return null
    return { buildId }
  } catch {
    return null
  }
}

/** Unregister service workers (if any) and wipe Cache Storage, then hard reload. */
export async function clearCachesAndReload(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister()))
    }
  } catch {
    // ignore
  }
  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
  } catch {
    // ignore
  }
  const url = new URL(window.location.href)
  url.searchParams.set('_v', String(Date.now()))
  window.location.replace(url.toString())
}
